import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { CellSelection } from "@tiptap/pm/tables";
import { Node } from "prosemirror-model";
import { countColumnsInActiveTable, countRowsInActiveTable } from "../lib/tableRowAndColumnCount";
import { autoSetTableColumnWidths } from "../utils/auto-table-widths";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableControls: {
      setSelectedCell: (tablePos: number, cellPos: number) => ReturnType;
      clearSelectedCell: () => ReturnType;
    };
  }
}

function resolveCell(state: EditorState) {
  const { $from } = state.selection;
  let cellPos = -1;
  let tablePos = -1;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    const pos = $from.start(depth);
    if (node.type.name === "tableCell" || node.type.name === "tableHeader") cellPos = pos - 1;
    if (node.type.name === "table") {
      tablePos = pos - 1;
      break;
    }
  }
  return { cellPos, tablePos };
}

function findCellDom(view: EditorView, cellPos: number): HTMLElement | null {
  try {
    const node = view.nodeDOM(cellPos);
    if (node instanceof HTMLElement) return node;
  } catch {
    console.error("Error finding cell DOM", cellPos);
  }
  return null;
}

function createBtn(className: string, onClick: () => void, text = "+", isDelete = false) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = className;
  btn.textContent = text;
  const width = isDelete ? "auto" : "18px";
  const padding = isDelete ? "padding:2px 6px;" : "";
  const fontSize = isDelete ? "9px" : "12px";
  const baseStyle = `position:absolute;width:${width};height:18px;border:1px solid;border-radius:${
    isDelete ? "8px" : "50%"
  };font-size:${fontSize};line-height:14px;cursor:pointer;z-index:10;box-shadow:0 1px 3px rgba(0,0,0,0.1);font-weight:bold;${padding}white-space:nowrap;pointer-events:auto;`;
  const deleteStyle = isDelete
    ? "border-color:#ef4444;background:#fee2e2;color:#dc2626;"
    : "border-color:#d1d5db;background:#ffffff;color:#374151;";
  btn.setAttribute("style", baseStyle + deleteStyle);
  btn.addEventListener("mousedown", (e) => e.preventDefault());
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  });
  return btn;
}

function layoutButtons(container: HTMLElement, cellRect: DOMRect, wrapperRect: DOMRect) {
  const top = cellRect.top - wrapperRect.top;
  const left = cellRect.left - wrapperRect.left;
  const w = cellRect.width;
  const h = cellRect.height;

  container.style.cssText =
    `position:absolute;pointer-events:none;display:block;top:${top}px;left:${left}px;width:${w}px;height:${h}px;`;

  const btns = container.children as HTMLCollectionOf<HTMLElement>;
  // rowBefore — top center
  Object.assign(btns[0].style, { left: "50%", top: "10px", right: "", bottom: "", transform: "translate(-50%,-100%)" });
  // rowAfter — bottom center
  Object.assign(btns[1].style, { left: "50%", bottom: "-10px", top: "", right: "", transform: "translateX(-50%)" });
  // colBefore — left center
  Object.assign(btns[2].style, { top: "50%", left: "10px", right: "", bottom: "", transform: "translate(-100%,-50%)" });
  // colAfter — right center
  Object.assign(btns[3].style, { top: "50%", right: "-10px", left: "", bottom: "", transform: "translateY(-50%)" });
  // deleteCol — top center (inside)
  Object.assign(btns[4].style, { top: "5px", left: "50%", right: "", bottom: "", transform: "translateX(-50%)" });
  // deleteRow — right center (inside)
  Object.assign(btns[5].style, { right: "10px", top: "50%", left: "", bottom: "", transform: "translateY(-50%)" });
}

export const TableControls = Extension.create({
  name: "tableControls",

  addStorage() {
    return {
      selectedTablePos: -1,
      selectedCellPos: -1
    };
  },

  addCommands() {
    return {
      setSelectedCell: (tablePos: number, cellPos: number) => () => {
        this.storage.selectedTablePos = tablePos;
        this.storage.selectedCellPos = cellPos;
        return true;
      },
      clearSelectedCell: () => () => {
        this.storage.selectedTablePos = -1;
        this.storage.selectedCellPos = -1;
        return true;
      }
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const storage = this.storage;

    return [
      new Plugin({
        key: new PluginKey("tableControlsView"),
        view(editorView) {
          const container = document.createElement("div");
          container.className = "table-controls-btns";
          container.style.display = "none";

          const runTableCmd = (fn: () => boolean) => {
            fn();
            setTimeout(() => autoSetTableColumnWidths(editor), 0);
          };
          container.appendChild(
            createBtn("tiptap-add-row-before-btn", () =>
              runTableCmd(() => editor.chain().focus().addRowBefore().run())
            )
          );
          container.appendChild(
            createBtn("tiptap-add-row-btn", () => runTableCmd(() => editor.chain().focus().addRowAfter().run()))
          );
          container.appendChild(
            createBtn("tiptap-add-col-before-btn", () =>
              runTableCmd(() => editor.chain().focus().addColumnBefore().run())
            )
          );
          container.appendChild(
            createBtn("tiptap-add-col-btn", () =>
              runTableCmd(() => editor.chain().focus().addColumnAfter().run())
            )
          );
          container.appendChild(
            createBtn(
              "tiptap-delete-col-btn",
              () => {
                runTableCmd(() =>
                  countColumnsInActiveTable(editor) > 1
                    ? editor.chain().focus().deleteColumn().run()
                    : editor.chain().focus().deleteTable().run()
                );
              },
              "-",
              true
            )
          );
          container.appendChild(
            createBtn(
              "tiptap-delete-row-btn",
              () => {
                runTableCmd(() =>
                  countRowsInActiveTable(editor) > 1
                    ? editor.chain().focus().deleteRow().run()
                    : editor.chain().focus().deleteTable().run()
                );
              },
              "-",
              true
            )
          );

          const wrapper = editorView.dom.parentNode as HTMLElement;
          if (wrapper) {
            wrapper.style.position = wrapper.style.position || "relative";
            wrapper.appendChild(container);
          }

          return {
            update(view: EditorView) {
              if (view.composing) return;

              const { cellPos, tablePos } = resolveCell(view.state);
              if (cellPos === -1 || tablePos === -1) {
                storage.selectedTablePos = -1;
                storage.selectedCellPos = -1;
                container.style.display = "none";
                return;
              }

              storage.selectedTablePos = tablePos;
              storage.selectedCellPos = cellPos;

              const cellEl = findCellDom(view, cellPos);
              const wrapperEl = view.dom.parentNode as HTMLElement;
              if (cellEl && wrapperEl) {
                const cellRect = cellEl.getBoundingClientRect();
                const wrapperRect = wrapperEl.getBoundingClientRect();
                layoutButtons(container, cellRect, wrapperRect);
              } else {
                container.style.display = "none";
              }
            },
            destroy() {
              container.remove();
            }
          };
        }
      }),

      new Plugin({
        key: new PluginKey("tableControlsKeyboard"),
        props: {
          handleKeyDown: (view, event) => {
            if (event.key !== "Backspace" && event.key !== "Delete") return false;

            const { selection } = view.state;

            if (!(selection instanceof CellSelection)) return false;

            const cells: { pos: number; node: Node }[] = [];
            selection.forEachCell((cell, pos: number) => {
              cells.push({ pos, node: cell });
            });

            if (cells.length === 0) return false;

            const isWholeRowSelected = isWholeRowSelectedFn(view.state, cells);
            const isWholeColumnSelected = isWholeColumnSelectedFn(view.state, cells);

            if (isWholeRowSelected) {
              event.preventDefault();
              editor.chain().focus().deleteRow().setTextSelection(0).run();
              setTimeout(() => autoSetTableColumnWidths(editor), 0);
              return true;
            }

            if (isWholeColumnSelected) {
              event.preventDefault();
              editor.chain().focus().deleteColumn().setTextSelection(0).run();
              setTimeout(() => autoSetTableColumnWidths(editor), 0);
              return true;
            }

            return false;
          }
        }
      })
    ];

    function isWholeRowSelectedFn(state: EditorState, selectedCells: { pos: number; node: Node }[]) {
      if (selectedCells.length === 0) return false;

      const firstCellPos = selectedCells[0].pos;
      const $pos = state.doc.resolve(firstCellPos);

      let tableNode: Node | null = null;
      let tablePos = -1;
      for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === "table") {
          tableNode = node;
          tablePos = $pos.start(depth);
          break;
        }
      }

      if (!tableNode) return false;

      let rowIndex = -1;
      let cellIndex = 0;
      tableNode.forEach((rowNode: Node, rowOffset: number) => {
        if (rowNode.type.name === "tableRow") {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          let cellCount = 0;
          rowNode.forEach((cellNode: Node, cellOffset: number) => {
            const cellPos = tablePos + rowOffset + cellOffset + 1;
            if (cellPos === firstCellPos) {
              rowIndex = cellIndex;
            }
            cellCount++;
          });
          if (rowIndex === -1) cellIndex++;
        }
      });

      if (rowIndex === -1) return false;

      let rowCellCount = 0;
      let currentRowIndex = 0;
      tableNode.forEach((rowNode: Node) => {
        if (rowNode.type.name === "tableRow") {
          if (currentRowIndex === rowIndex) {
            rowNode.forEach(() => rowCellCount++);
          }
          currentRowIndex++;
        }
      });

      return selectedCells.length === rowCellCount;
    }

    function isWholeColumnSelectedFn(state: EditorState, selectedCells: { pos: number; node: Node }[]) {
      if (selectedCells.length === 0) return false;

      const firstCellPos = selectedCells[0].pos;
      const $pos = state.doc.resolve(firstCellPos);

      let tableNode: Node | null = null;
      let tablePos = -1;
      for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === "table") {
          tableNode = node;
          tablePos = $pos.start(depth);
          break;
        }
      }

      if (!tableNode) return false;

      let columnIndex = -1;
      let rowCount = 0;
      tableNode.forEach((rowNode: Node, rowOffset: number) => {
        if (rowNode.type.name === "tableRow") {
          let cellIndex = 0;
          rowNode.forEach((cellNode: Node, cellOffset: number) => {
            const cellPos = tablePos + rowOffset + cellOffset + 1;
            if (cellPos === firstCellPos) {
              columnIndex = cellIndex;
            }
            cellIndex++;
          });
          rowCount++;
        }
      });

      if (columnIndex === -1) return false;

      return selectedCells.length === rowCount;
    }
  },

  onCreate() {
    const style = document.createElement("style");
    style.textContent = `
      .ProseMirror td, .ProseMirror th {
        position: relative;
        overflow: visible;
        word-break: break-word;
        overflow-wrap: break-word;
        min-width: 0;
      }
      .ProseMirror table {
        overflow: visible;
        width: 100%;
        table-layout: fixed;
      }
      .ProseMirror tr {
        overflow: visible;
      }
      .ProseMirror table a {
        word-break: break-word;
        overflow-wrap: break-word;
      }
      .tiptap-add-row-btn,
      .tiptap-add-row-before-btn,
      .tiptap-add-col-btn,
      .tiptap-add-col-before-btn,
      .tiptap-delete-row-btn,
      .tiptap-delete-col-btn {
        pointer-events: auto;
        transition: all 0.2s ease;
      }
      .tiptap-delete-row-btn:hover,
      .tiptap-delete-col-btn:hover {
        background: #fecaca !important;
        transform: scale(1.05);
      }
      .tiptap-delete-row-btn:hover ~ tr,
      .tiptap-delete-row-btn:hover {
        position: relative;
      }
      .tiptap-delete-col-btn:hover {
        position: relative;
      }
    `;
    document.head.appendChild(style);
  }
});
